import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from '@Sage/hoi-poi-ui';

import { EntityFiltersActions, EntityListActions, EntityListSelectActions } from 'actions';
import Accordion from 'components/Accordion';
import { COMPANIES } from 'constants/Entities';
import { formatDate } from 'utils/dates';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';

import AccountList, { CAMPAIGN_ACCOUNTS_FILTER_KEY } from './AccountList';
import AccountsItemSubtitle from './AccountsItemSubtitle';
import FormDetails from './FormDetails';
import QuestionsCrud from './QuestionsCrud';

const AccordionCrud = memo(
    ({ data, onChange, errors, firstErrorField, setCheckErrors, schema }) => {
        const dispatch = useDispatch();
        const questionsRef = useRef(null);
        const prevCampaignIdRef = useRef(data?.id);

        const selection = useSelector((state) => state.entityListSelect[COMPANIES.entity] || {});
        const selectionToken = selection.token;
        const selectedAccounts = selection.total > 0 ? selection.total : null;
        const selectedInfo = selection.info;
        const selectedInfoLoading = selection.infoLoading;

        const [questionsOpen, setQuestionsOpen] = useState(false);
        const [accountsOpen, setAccountsOpen] = useState(false);
        const [questionsError, setQuestionsError] = useState({});
        const [questions, setQuestions] = useState([...(data.campaignQuestions || [])]);

        const resetCampaignAccountsFilters = useCallback(() => {
            dispatch(EntityListActions.setFilterStateKey(COMPANIES, CAMPAIGN_ACCOUNTS_FILTER_KEY));
            dispatch(
                EntityFiltersActions.clearFilters({
                    entity: COMPANIES,
                    filterKey: CAMPAIGN_ACCOUNTS_FILTER_KEY,
                    isAPurge: false,
                    refresh: false,
                }),
            );
            dispatch(EntityFiltersActions.hideFilters(COMPANIES, CAMPAIGN_ACCOUNTS_FILTER_KEY));
            dispatch(EntityListSelectActions.resetWeb5HeaderSelectionState(COMPANIES));
        }, [dispatch]);

        const fetchSelectionInfo = useCallback(
            (token) => {
                if (!token) return;
                dispatch(
                    EntityListSelectActions.getSelectionInfo({
                        entity: COMPANIES,
                        token,
                        selectionFor: 'campaigns',
                        selectionEntity: 'accounts',
                    }),
                );
            },
            [dispatch],
        );

        useEffect(() => {
            resetCampaignAccountsFilters();

            if (data.statusId === 2) {
                dispatch(EntityListSelectActions.changeSelectMode(COMPANIES, 'update'));
            }
            fetchSelectionInfo(data.token);

            return () => {
                dispatch(
                    EntityFiltersActions.clearFilters({
                        entity: COMPANIES,
                        filterKey: CAMPAIGN_ACCOUNTS_FILTER_KEY,
                        isAPurge: false,
                        refresh: false,
                    }),
                );
                dispatch(EntityFiltersActions.hideFilters(COMPANIES, CAMPAIGN_ACCOUNTS_FILTER_KEY));
                dispatch(EntityListActions.setFilterStateKey(COMPANIES, null));
            };
            // Mount/unmount only — picker filters persist while this campaign CRUD is open.
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        useEffect(() => {
            const prevId = prevCampaignIdRef.current;
            const nextId = data?.id;
            prevCampaignIdRef.current = nextId;

            // Edit campaign A → edit campaign B without remounting AccordionCrud.
            if (prevId && nextId && prevId !== nextId) {
                resetCampaignAccountsFilters();
            }
        }, [data?.id, resetCampaignAccountsFilters]);

        const onAccountsOpen = useCallback(() => {
            dispatch(EntityListActions.setFilterStateKey(COMPANIES, CAMPAIGN_ACCOUNTS_FILTER_KEY));
            dispatch(EntityFiltersActions.hideFilters(COMPANIES, CAMPAIGN_ACCOUNTS_FILTER_KEY));
            setAccountsOpen(true);
        }, [dispatch]);

        const onAccountsComplete = useCallback(() => {
            onChange('token')(selectionToken);
            if (selectionToken) fetchSelectionInfo(selectionToken);
            setAccountsOpen(false);
        }, [onChange, selectionToken, fetchSelectionInfo]);

        const onAccountsClose = useCallback(() => {
            dispatch(EntityListSelectActions.cancelSelection());
            onAccountsComplete();
        }, [dispatch, onAccountsComplete]);

        const onQuestionsClose = useCallback(() => {
            setQuestionsOpen(false);
        }, []);

        const onQuestionsOpen = useCallback(() => {
            setQuestionsOpen(true);
        }, []);

        const onChangeQuestions = useCallback((nextQuestions) => {
            setQuestions(nextQuestions);
        }, []);

        const checkErrors = useCallback((campaignQuestions = []) => {
            let someError = false;
            const nextQuestionsError = campaignQuestions.reduce((obj, q) => {
                const typeError = !q.properties.type ? 'mandatory' : null;
                const textError = !q.text ? 'mandatory' : null;
                if (typeError || textError) someError = true;
                obj[q._id] = {
                    type: typeError,
                    text: textError,
                };
                return obj;
            }, {});

            setQuestionsError(nextQuestionsError);
            return someError;
        }, []);

        const onQuestionsComplete = useCallback(() => {
            const innerQuestions = [...(questions || [])];
            if (checkErrors(questions)) return;

            // Mutate order for avoid rerenderings.
            innerQuestions.forEach((q, i) => (q.properties.order = i + 1));
            const cleanQuestions = innerQuestions.map((question) => {
                if (question.properties && question.properties.items) {
                    question.properties.items = question.properties.items.filter(
                        (item) => !!item.value,
                    );
                }
                return question;
            });
            onChange('campaignQuestions')(cleanQuestions);
            setQuestionsOpen(false);
            setQuestionsError({});
        }, [questions, checkErrors, onChange]);

        const scrollQuestionsBottom = useCallback(() => {
            setTimeout(() => {
                questionsRef.current?.scrollTo(0, questionsRef.current.scrollHeight);
            }, 0);
        }, []);

        const renderDetailForm = useCallback(
            (onToggle) => (
                <FormDetails
                    schema={schema}
                    data={data}
                    errors={errors}
                    onSave={onChange()}
                    onToggle={onToggle}
                    setCheckErrors={setCheckErrors}
                />
            ),
            [schema, data, errors, onChange, setCheckErrors],
        );

        const detailsSubtitle = useMemo(() => {
            let subtitle = getLiteral('label_campaigns_crud_details_helptext');
            const startDate = data.campaignDate ? data.campaignDate.campaignStartDate : null;

            if (data.descriptionRich && data.descriptionRich.text && !startDate) {
                subtitle = (
                    <span className="campaign-accordion-subtitle">{data.descriptionRich.text}</span>
                );
            } else if (data.descriptionRich && data.descriptionRich.text && startDate) {
                subtitle = (
                    <span className="campaign-accordion-subtitle">
                        <span className="campaign-subtitle-description">
                            {data.descriptionRich.text}
                        </span>
                        <span className="campaign-subtitle-date">
                            {getLiteral('label_campaigns_define_start_date')}
                            {` ${formatDate(startDate, 'll')}`}
                        </span>
                    </span>
                );
            } else if ((!data.descriptionRich || !data.descriptionRich.text) && startDate) {
                subtitle = (
                    <span className="campaign-accordion-subtitle">
                        {getLiteral('label_campaigns_define_start_date')}
                        {` ${formatDate(startDate, 'll')}`}
                    </span>
                );
            }

            return subtitle;
        }, [data.campaignDate, data.descriptionRich]);

        const accountsSubtitle = useMemo(
            () => (
                <AccountsItemSubtitle
                    selectedInfoLoading={selectedInfoLoading}
                    accountOwners={selectedInfo?.ownersName || []}
                    accountNumber={selectedInfo?.entities || 0}
                />
            ),
            [selectedInfo, selectedInfoLoading],
        );

        const questionsSubtitle = useMemo(() => {
            if (questions.length > 0) {
                return getLiteralWithParameters('label_campaigns_total_questions', [
                    questions.length,
                ]);
            }
            return getLiteral('label_campaigns_crud_questions_helptext');
        }, [questions.length]);

        const detailsTitle = useMemo(() => {
            let someError = false;
            if (!data?.typeId) someError = true;
            if (!data?.descriptionRich?.text) someError = true;
            if (!data?.campaignDate) someError = true;

            const startDate = data.campaignDate ? data.campaignDate.campaignStartDate : null;
            const endDate = data.campaignDate ? data.campaignDate.campaignEndDate : null;

            someError =
                firstErrorField &&
                (someError ||
                    !startDate ||
                    !endDate ||
                    (errors.campaignDate && typeof errors.campaignDate === 'string'));

            return (
                <span className={someError ? 'fm-border-required' : ''}>
                    {getLiteral('label_campaigns_crud_details')}
                </span>
            );
        }, [data, errors, firstErrorField]);

        const accountsTitle = useMemo(() => {
            let someError = false;

            if (errors.companiesSelect) {
                if (data?.token && selectedAccounts) someError = false;
                else someError = true;
            } else if (data?.token && !selectedAccounts && !selectedInfoLoading) {
                someError = true;
            }

            return (
                <span className={someError ? 'fm-border-required' : ''}>
                    {getLiteral('label_campaigns_crud_accounts')}
                </span>
            );
        }, [errors.companiesSelect, data?.token, selectedAccounts, selectedInfoLoading]);

        const items = useMemo(() => {
            let detailsButton = 'action_edit';
            let accountsButton = 'action_campaigns_crud_accounts_select';
            let questionsButton = 'action_campaigns_crud_questions_add';

            if (selectedAccounts) {
                accountsButton = 'action_modify';
            }

            if (questions.length) {
                questionsButton = 'action_modify';
            }

            if (data.campaignDate || data.typeId || data.descriptionRich) {
                detailsButton = 'action_modify';
            }

            return [
                {
                    title: detailsTitle,
                    subtitle: detailsSubtitle,
                    buttonText: getLiteral(detailsButton),
                    buttonClass: 'accordion-campaigns-button__edit',
                    isRequired: true,
                    children: renderDetailForm,
                },
                {
                    title: accountsTitle,
                    subtitle: accountsSubtitle,
                    buttonText: getLiteral(accountsButton),
                    buttonClass: 'accordion-campagins-button__select',
                    onClick: onAccountsOpen,
                    isRequired: true,
                },
                {
                    title: getLiteral('label_campaigns_crud_questions'),
                    subtitle: questionsSubtitle,
                    buttonText: getLiteral(questionsButton),
                    buttonClass: 'accordion-campaigns-button__add',
                    onClick: onQuestionsOpen,
                    isDisabled: data.statusId === 2,
                    isOptional: true,
                },
            ];
        }, [
            selectedAccounts,
            questions.length,
            data.campaignDate,
            data.typeId,
            data.descriptionRich,
            data.statusId,
            detailsTitle,
            detailsSubtitle,
            renderDetailForm,
            accountsTitle,
            accountsSubtitle,
            onAccountsOpen,
            questionsSubtitle,
            onQuestionsOpen,
        ]);

        const questionsModalLiteral = questions.length
            ? 'label_campaigns_update_questions'
            : 'label_campaigns_crud_questions_add_questions_add';

        const accountsConfirmText =
            selectedAccounts > 0
                ? `${getLiteral('action_campaigns_done')} ${selectedAccounts}`
                : getLiteral('action_campaigns_done');

        const questionsModalOverrides = useMemo(
            () => ({
                content: {
                    ref: (ref) => {
                        questionsRef.current = ref;
                    },
                    style: {
                        overflow: 'auto',
                        position: 'relative',
                        height: '100%',
                    },
                },
            }),
            [],
        );

        return (
            <div className="accordion-campaigns-crud">
                <Accordion items={items} optionalLabel={getLiteral('label_optional')} />
                <Modal
                    size="full"
                    title={getLiteral('label_campaigns_crud_questions_add_questions_add')}
                    className="campaign-questions"
                    isOpen={questionsOpen}
                    onRequestClose={onQuestionsClose}
                    onCancel={onQuestionsClose}
                    onConfirm={onQuestionsComplete}
                    confirmText={`${getLiteral(questionsModalLiteral)} ${questions.length || 0}`}
                    isConfirmDisabled={
                        !questions || !questions.length || !questions[0] || !questions[0].text
                    }
                    cancelText={getLiteral('action_cancel')}
                    overrides={questionsModalOverrides}
                >
                    <QuestionsCrud
                        questions={questions}
                        errors={questionsError}
                        checkErrors={checkErrors}
                        onChange={onChangeQuestions}
                        scrollBottom={scrollQuestionsBottom}
                    />
                </Modal>
                <Modal
                    size="full"
                    title={getLiteral('label_campaigns_crud_accounts')}
                    className="campaign-accounts"
                    isOpen={accountsOpen}
                    onRequestClose={onAccountsClose}
                    onConfirm={onAccountsComplete}
                    confirmText={accountsConfirmText}
                    isConfirmDisabled={!selectedAccounts}
                >
                    <AccountList selectionToken={data.token} />
                </Modal>
            </div>
        );
    },
);

AccordionCrud.displayName = 'AccordionCrud';

export default AccordionCrud;
